import { Severity } from './missionTokens';

/**
 * Mission Control's data contract.
 *
 * Typed interfaces first, mock values behind them. Every component on the home
 * screen reads these shapes, so wiring a real endpoint later is a matter of
 * replacing the loader — not rewriting the UI. Nothing here is rendered from a
 * literal inside a component.
 */

export interface PulseMetric {
  key: string;
  label: string;
  value: string;
  delta: number;
  /** false where a rise is bad (cost, overdue, attrition). */
  higherIsBetter?: boolean;
  icon: 'revenue' | 'cash' | 'orders' | 'efficiency' | 'people';
}

export interface FlowStage {
  key: string;
  label: string;
  count: string;
  delta: number;
  href: string;
}

export interface AttentionItem {
  id: string;
  title: string;
  detail: string;
  severity: Severity;
  actionLabel: string;
  href: string;
}

export interface ScheduleItem {
  time: string;
  title: string;
  detail: string;
  tone: 'primary' | 'violet' | 'success' | 'warning';
}

export interface ActivityItem {
  title: string;
  detail: string;
  when: string;
  kind: 'approval' | 'order' | 'grn' | 'people' | 'invoice';
}

export interface MarginDriver {
  label: string;
  delta: number;
}

/** The perspectives that reshape the workspace. */
export const PERSPECTIVES = [
  'Executive', 'Financial', 'Operations', 'Sales', 'Supply Chain',
  'People', 'Risk', 'Customer', 'Sustainability', 'Custom',
] as const;

export type Perspective = (typeof PERSPECTIVES)[number];

/**
 * Pulse metrics per perspective.
 *
 * Deliberately a different *set* of metrics per perspective, not the same five
 * cards re-filtered — the brief is explicit that switching perspective should
 * change the information architecture, not just the data behind it.
 */
const PULSE: Partial<Record<Perspective, PulseMetric[]>> = {
  Executive: [
    { key: 'rev', label: 'Revenue', value: '₹12.48 Cr', delta: 12, icon: 'revenue' },
    { key: 'cash', label: 'Cash & Bank', value: '₹7.74 Cr', delta: 8, icon: 'cash' },
    { key: 'orders', label: 'Open Orders', value: '482', delta: 14, icon: 'orders' },
    { key: 'oee', label: 'Production Efficiency', value: '92%', delta: 7, icon: 'efficiency' },
    { key: 'emp', label: 'Active Employees', value: '1,248', delta: 6, icon: 'people' },
  ],
  Financial: [
    { key: 'rev', label: 'Revenue', value: '₹24.80 Cr', delta: 18, icon: 'revenue' },
    { key: 'gp', label: 'Gross Profit', value: '₹4.56 Cr', delta: 10, icon: 'revenue' },
    { key: 'ebitda', label: 'EBITDA Margin', value: '18.4%', delta: 2.1, icon: 'efficiency' },
    { key: 'ar', label: 'Receivables', value: '₹3.84 Cr', delta: 18, higherIsBetter: false, icon: 'cash' },
    { key: 'ap', label: 'Payables', value: '₹2.19 Cr', delta: -4, higherIsBetter: false, icon: 'cash' },
  ],
  Operations: [
    { key: 'oee', label: 'Production Efficiency', value: '92%', delta: 7, icon: 'efficiency' },
    { key: 'wo', label: 'Open Work Orders', value: '64', delta: 7, icon: 'orders' },
    { key: 'qc', label: 'First-Pass Yield', value: '96.2%', delta: 1.4, icon: 'efficiency' },
    { key: 'stock', label: 'Stock Value', value: '₹3.46 Cr', delta: -3, icon: 'cash' },
    { key: 'dispatch', label: 'Dispatches', value: '211', delta: 5, icon: 'orders' },
  ],
  Sales: [
    { key: 'pipeline', label: 'Pipeline', value: '₹18.2 Cr', delta: 9, icon: 'revenue' },
    { key: 'so', label: 'Sales Orders', value: '482', delta: 8, icon: 'orders' },
    { key: 'win', label: 'Win Rate', value: '34%', delta: 3, icon: 'efficiency' },
    { key: 'aov', label: 'Avg Order Value', value: '₹2.59 L', delta: 4, icon: 'revenue' },
    { key: 'churn', label: 'At-Risk Accounts', value: '7', delta: 2, higherIsBetter: false, icon: 'people' },
  ],
};

/** Perspectives without a bespoke set fall back to the executive view. */
export const pulseFor = (p: Perspective): PulseMetric[] => PULSE[p] ?? PULSE.Executive!;

export const FLOW: FlowStage[] = [
  { key: 'leads', label: 'Leads', count: '128', delta: 12, href: '/sales/leads' },
  { key: 'so', label: 'Sales Orders', count: '482', delta: 8, href: '/sales/orders' },
  { key: 'proc', label: 'Procurement', count: '128', delta: -4, href: '/procurement/orders' },
  { key: 'prod', label: 'Production', count: '64', delta: 7, href: '/manufacturing/orders' },
  { key: 'qc', label: 'Quality', count: '58', delta: 2, href: '/quality' },
  { key: 'inv', label: 'Inventory', count: '3,462', delta: -3, href: '/inventory/stock' },
  { key: 'dispatch', label: 'Dispatch', count: '211', delta: 5, href: '/logistics' },
  { key: 'invoice', label: 'Invoice', count: '198', delta: 12, href: '/sales/invoices' },
  { key: 'payment', label: 'Payment', count: '176', delta: 18, href: '/finance/payments' },
];

export const ATTENTION: AttentionItem[] = [
  {
    id: 'ar-overdue',
    title: '12 invoices are overdue',
    detail: '₹38.4 L outstanding · 4 key customers',
    severity: 'High',
    actionLabel: 'Review',
    href: '/finance/receivables',
  },
  {
    id: 'stock-risk',
    title: 'Inventory risk detected',
    detail: '3 materials may fall below safety stock',
    severity: 'Medium',
    actionLabel: 'View Materials',
    href: '/inventory/stock',
  },
  {
    id: 'po-approvals',
    title: '7 purchase approvals pending',
    detail: 'Total value ₹21.8 L',
    severity: 'Medium',
    actionLabel: 'Review',
    href: '/approvals',
  },
  {
    id: 'attrition',
    title: 'Engineering attrition risk',
    detail: '5 key employees',
    severity: 'Low',
    actionLabel: 'Explore',
    href: '/hrms/employees',
  },
];

export const SCHEDULE: ScheduleItem[] = [
  { time: '09:30 AM', title: 'Operations Review', detail: 'Conf Room A', tone: 'primary' },
  { time: '11:00 AM', title: 'Vendor Discussion', detail: 'Microsoft Teams', tone: 'violet' },
  { time: '02:00 PM', title: 'Budget Planning', detail: 'Finance Team', tone: 'success' },
  { time: '04:00 PM', title: 'Product Roadmap', detail: 'Leadership Call', tone: 'warning' },
];

export const ACTIVITY: ActivityItem[] = [
  { title: 'PO-2026-0712 approved', detail: 'by Rohan Sharma', when: '10:24 AM', kind: 'approval' },
  { title: 'SO-2026-4482 created', detail: 'by Neha Verma', when: '09:41 AM', kind: 'order' },
  { title: 'Material GRN completed', detail: 'Main Warehouse', when: '09:12 AM', kind: 'grn' },
  { title: 'Employee onboarded', detail: 'Karan Singh', when: 'Yesterday', kind: 'people' },
  { title: 'Invoice INV-3381 paid', detail: 'by Global Auto Ltd.', when: 'Yesterday', kind: 'invoice' },
];

/** The worked example from the brief: why operating margin moved. */
export const MARGIN_STORY = {
  question: 'Why did operating margin fall 2.3% this month?',
  drivers: [
    { label: 'Revenue', delta: 4.2 },
    { label: 'Material Cost', delta: 8.7 },
    { label: 'Logistics', delta: 5.1 },
    { label: 'Labor Cost', delta: 2.4 },
  ] as MarginDriver[],
  primaryDriver: { label: 'Raw Material Cost', impact: '+₹12.4 L' },
  actions: ['Compare Suppliers', 'Review Purchase History', 'Create Report'],
};

/** Revenue by month, ₹ Cr — Jan..Sep. */
export const REVENUE_SERIES = [8.2, 9.1, 12.4, 13.0, 15.6, 17.2, 19.4, 22.1, 24.8];
export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep'];

export const AI_SUGGESTIONS = [
  'Why did operating margin fall this month?',
  'Show me pending purchase approvals',
  'Which customers are overdue?',
  'Forecast next quarter’s demand',
  'Generate a financial summary',
];
